import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { v4 as uuidv4 } from 'uuid';

// Types
import { Presentation, Question } from './slices/presentationSlice';
import { User } from './slices/userSlice';
import { Vote, VoteResult } from './slices/votingSlice';

// Mock data
import { 
  mockPresentations, 
  mockUsers, 
  mockVotes,
  initializeMockData,
  saveToLocalStorage,
  loadFromLocalStorage,
  MockPresentation,
  MockUser,
  MockVote
} from '../data/mockData';

// Initialize mock data on app start
initializeMockData();

// Helper function to convert mock data to app format
const convertMockPresentation = (mockPresentation: MockPresentation): Presentation => ({
  id: mockPresentation.id,
  title: mockPresentation.title,
  presenter: mockPresentation.presenter,
  description: mockPresentation.description,
  date: mockPresentation.date,
  status: mockPresentation.status,
  questions: mockPresentation.questions.map(q => ({
    id: q.id,
    text: q.text,
    type: q.type,
    options: q.options,
    required: q.required,
    order: q.order
  })),
  createdAt: mockPresentation.createdAt,
  updatedAt: mockPresentation.updatedAt
});

const convertMockUser = (mockUser: MockUser): User => ({
  id: mockUser.id,
  username: mockUser.username,
  email: mockUser.email,
  firstName: mockUser.attributes.given_name,
  lastName: mockUser.attributes.family_name,
  role: mockUser.attributes['custom:role'],
  department: 'Investment',
  createdAt: '2023-01-01T00:00:00Z',
  updatedAt: '2023-01-01T00:00:00Z'
});

// Mock API handlers using localStorage
const mockApiHandlers = {
  // Presentations
  getPresentations: (): Presentation[] => {
    const presentations = loadFromLocalStorage('presentations', mockPresentations);
    return presentations.map(convertMockPresentation);
  },
  
  getPresentationById: (id: string): Presentation => {
    const presentations = loadFromLocalStorage('presentations', mockPresentations);
    const presentation = presentations.find((p: MockPresentation) => p.id === id);
    if (!presentation) throw new Error('Presentation not found');
    return convertMockPresentation(presentation);
  },
  
  createPresentation: (presentation: Partial<Presentation>): Presentation => {
    const presentations = loadFromLocalStorage('presentations', mockPresentations);
    const newPresentation: MockPresentation = {
      id: uuidv4(),
      title: presentation.title || '',
      description: presentation.description || '',
      presenter: presentation.presenter || '',
      date: presentation.date || new Date().toISOString().split('T')[0],
      status: presentation.status || 'draft',
      questions: presentation.questions?.map(q => ({
        id: q.id || uuidv4(),
        text: q.text,
        type: q.type,
        options: q.options,
        required: q.required,
        order: q.order
      })) || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    presentations.push(newPresentation);
    saveToLocalStorage('presentations', presentations);
    return convertMockPresentation(newPresentation);
  },
  
  updatePresentation: (presentation: Partial<Presentation> & { id: string }): Presentation => {
    const presentations = loadFromLocalStorage('presentations', mockPresentations);
    const index = presentations.findIndex((p: MockPresentation) => p.id === presentation.id);
    if (index === -1) throw new Error('Presentation not found');
    
    const updatedPresentation: MockPresentation = {
      ...presentations[index],
      ...presentation,
      questions: presentation.questions?.map(q => ({
        id: q.id || uuidv4(),
        text: q.text,
        type: q.type,
        options: q.options,
        required: q.required,
        order: q.order
      })) || presentations[index].questions,
      updatedAt: new Date().toISOString(),
    };
    
    presentations[index] = updatedPresentation;
    saveToLocalStorage('presentations', presentations);
    return convertMockPresentation(updatedPresentation);
  },
  
  deletePresentation: (id: string) => {
    const presentations = loadFromLocalStorage('presentations', mockPresentations);
    const index = presentations.findIndex((p: MockPresentation) => p.id === id);
    if (index === -1) throw new Error('Presentation not found');
    
    presentations.splice(index, 1);
    saveToLocalStorage('presentations', presentations);
    return { success: true };
  },
  
  // Questions
  getQuestions: (presentationId: string): Question[] => {
    const presentations = loadFromLocalStorage('presentations', mockPresentations);
    const presentation = presentations.find((p: MockPresentation) => p.id === presentationId);
    if (!presentation) throw new Error('Presentation not found');
    return presentation.questions;
  },
  
  // Votes
  getVotes: ({ presentationId, questionId }: { presentationId: string; questionId: string }) => {
    const votes = loadFromLocalStorage('votes', mockVotes);
    const questionVotes = votes.filter((v: MockVote) => v.questionId === questionId);
    
    // Calculate results
    const presentations = loadFromLocalStorage('presentations', mockPresentations);
    const presentation = presentations.find((p: MockPresentation) => p.id === presentationId);
    const question = presentation?.questions.find((q: any) => q.id === questionId);
    
    if (!question) {
      return { votes: [], result: { questionId, optionCounts: [], totalVotes: 0 } };
    }
    
    const optionCounts = Array(question.options.length).fill(0);
    questionVotes.forEach((vote: MockVote) => {
      if (vote.selectedOption < optionCounts.length) {
        optionCounts[vote.selectedOption]++;
      }
    });
    
    const result: VoteResult = {
      questionId,
      optionCounts,
      totalVotes: questionVotes.length,
    };
    
    const convertedVotes: Vote[] = questionVotes.map((v: MockVote) => ({
      id: v.id,
      questionId: v.questionId,
      userId: v.userId,
      optionIndex: v.selectedOption,
      timestamp: v.timestamp,
    }));
    
    return { votes: convertedVotes, result };
  },
  
  submitVote: ({ presentationId, questionId, optionIndex }: { presentationId: string; questionId: string; optionIndex: number }) => {
    const votes = loadFromLocalStorage('votes', mockVotes);
    const userId = '1'; // Mock current user
    
    // Remove existing vote from this user if it exists
    const filteredVotes = votes.filter((v: MockVote) => !(v.userId === userId && v.questionId === questionId));
    
    // Create new vote
    const newVote: MockVote = {
      id: uuidv4(),
      userId,
      presentationId,
      questionId,
      selectedOption: optionIndex,
      timestamp: new Date().toISOString(),
    };
    
    filteredVotes.push(newVote);
    saveToLocalStorage('votes', filteredVotes);
    
    // Return updated results
    const { result } = mockApiHandlers.getVotes({ presentationId, questionId });
    
    return { 
      vote: {
        id: newVote.id,
        questionId: newVote.questionId,
        userId: newVote.userId,
        optionIndex: newVote.selectedOption,
        timestamp: newVote.timestamp,
      }, 
      result 
    };
  },
  
  // Users
  getUsers: (): User[] => {
    const users = loadFromLocalStorage('users', mockUsers);
    return users.map(convertMockUser);
  },
  
  getUserById: (id: string): User => {
    const users = loadFromLocalStorage('users', mockUsers);
    const user = users.find((u: MockUser) => u.id === id);
    if (!user) throw new Error('User not found');
    return convertMockUser(user);
  },
};

// Define base API configuration
export const api = createApi({
  reducerPath: 'api',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Presentations', 'Questions', 'Votes', 'Users'],
  endpoints: (builder) => ({
    // Presentations
    getPresentations: builder.query<Presentation[], void>({
      queryFn: async () => {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 300));
        try {
          const data = mockApiHandlers.getPresentations();
          return { data };
        } catch (error) {
          return { error: { status: 500, data: (error as Error).message } };
        }
      },
      providesTags: ['Presentations'],
    }),
    
    getPresentationById: builder.query<Presentation, string>({
      queryFn: async (id) => {
        await new Promise(resolve => setTimeout(resolve, 200));
        try {
          const data = mockApiHandlers.getPresentationById(id);
          return { data };
        } catch (error) {
          return { error: { status: 404, data: (error as Error).message } };
        }
      },
      providesTags: (result, error, id) => [{ type: 'Presentations', id }],
    }),
    
    createPresentation: builder.mutation<Presentation, Partial<Presentation>>({
      queryFn: async (presentation) => {
        await new Promise(resolve => setTimeout(resolve, 500));
        try {
          const data = mockApiHandlers.createPresentation(presentation);
          return { data };
        } catch (error) {
          return { error: { status: 500, data: (error as Error).message } };
        }
      },
      invalidatesTags: ['Presentations'],
    }),
    
    updatePresentation: builder.mutation<Presentation, Partial<Presentation> & { id: string }>({
      queryFn: async (presentation) => {
        await new Promise(resolve => setTimeout(resolve, 500));
        try {
          const data = mockApiHandlers.updatePresentation(presentation);
          return { data };
        } catch (error) {
          return { error: { status: 500, data: (error as Error).message } };
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Presentations', id },
        'Presentations',
      ],
    }),
    
    deletePresentation: builder.mutation<{ success: boolean }, string>({
      queryFn: async (id) => {
        await new Promise(resolve => setTimeout(resolve, 300));
        try {
          const data = mockApiHandlers.deletePresentation(id);
          return { data };
        } catch (error) {
          return { error: { status: 500, data: (error as Error).message } };
        }
      },
      invalidatesTags: ['Presentations'],
    }),

    // Questions
    getQuestions: builder.query<Question[], string>({
      queryFn: async (presentationId) => {
        await new Promise(resolve => setTimeout(resolve, 200));
        try {
          const data = mockApiHandlers.getQuestions(presentationId);
          return { data };
        } catch (error) {
          return { error: { status: 404, data: (error as Error).message } };
        }
      },
      providesTags: ['Questions'],
    }),

    // Votes
    getVotes: builder.query<{ votes: Vote[]; result: VoteResult }, { presentationId: string; questionId: string }>({
      queryFn: async ({ presentationId, questionId }) => {
        await new Promise(resolve => setTimeout(resolve, 200));
        try {
          const data = mockApiHandlers.getVotes({ presentationId, questionId });
          return { data };
        } catch (error) {
          return { error: { status: 500, data: (error as Error).message } };
        }
      },
      providesTags: ['Votes'],
    }),
    
    submitVote: builder.mutation<{ vote: Vote; result: VoteResult }, { presentationId: string; questionId: string; optionIndex: number }>({
      queryFn: async ({ presentationId, questionId, optionIndex }) => {
        await new Promise(resolve => setTimeout(resolve, 300));
        try {
          const data = mockApiHandlers.submitVote({ presentationId, questionId, optionIndex });
          return { data };
        } catch (error) {
          return { error: { status: 500, data: (error as Error).message } };
        }
      },
      invalidatesTags: ['Votes'],
    }),
    
    // Users
    getUsers: builder.query<User[], void>({
      queryFn: async () => {
        await new Promise(resolve => setTimeout(resolve, 200));
        try {
          const data = mockApiHandlers.getUsers();
          return { data };
        } catch (error) {
          return { error: { status: 500, data: (error as Error).message } };
        }
      },
      providesTags: ['Users'],
    }),
    
    getUserById: builder.query<User, string>({
      queryFn: async (id) => {
        await new Promise(resolve => setTimeout(resolve, 200));
        try {
          const data = mockApiHandlers.getUserById(id);
          return { data };
        } catch (error) {
          return { error: { status: 404, data: (error as Error).message } };
        }
      },
      providesTags: (result, error, id) => [{ type: 'Users', id }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  // Presentations
  useGetPresentationsQuery,
  useGetPresentationByIdQuery,
  useCreatePresentationMutation,
  useUpdatePresentationMutation,
  useDeletePresentationMutation,
  
  // Questions
  useGetQuestionsQuery,
  
  // Votes
  useGetVotesQuery,
  useSubmitVoteMutation,
  
  // Users
  useGetUsersQuery,
  useGetUserByIdQuery,
} = api;
